import { apiClient } from '../axios-instance'
import type { AxiosResponse } from 'axios'

export interface LoginRequest {
  email: string
  password: string
  device_name?: string
}

export interface MfaVerifyRequest {
  email: string
  otp: string
}

export interface LoginResponse {
  success: boolean
  message?: string
  data?: {
    mfa_required?: boolean
    otp?: string
    access_token?: string
    refresh_token?: string
    expires_in?: number
    user?: {
      id: string
      email: string
      role: string
      permissions?: string[]
    }
  }
}

export interface RefreshTokenRequest {
  refresh_token: string
}

export interface LogoutRequest {
  device_id?: string
}

/**
 * Authentication Service
 * Handles admin login, MFA verification, logout, token refresh
 */
export const authService = {
  /**
   * Admin login (Step 1 - sends MFA code)
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await apiClient.post(
      '/auth/admin/login',
      data
    )
    return response.data
  },

  /**
   * Verify MFA code (Step 2 - completes login)
   */
  verifyMfa: async (data: MfaVerifyRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await apiClient.post(
      '/auth/admin/mfa/verify',
      data
    )
    return response.data
  },

  /**
   * Admin logout
   */
  logout: async (data?: LogoutRequest): Promise<void> => {
    await apiClient.post('/auth/admin/logout', data)
  },

  /**
   * Refresh access token
   */
  refreshToken: async (data: RefreshTokenRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await apiClient.post(
      '/auth/admin/refresh',
      data
    )
    return response.data
  },

  /**
   * Get current admin profile
   */
  getProfile: async (): Promise<any> => {
    const response = await apiClient.get('/auth/admin/profile')
    return response.data
  },

  /**
   * Verify admin session
   */
  verifySession: async (): Promise<boolean> => {
    try {
      await apiClient.get('/admin/auth/verify')
      return true
    } catch {
      return false
    }
  },

  /**
   * Change admin password
   */
  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
    await apiClient.post('/admin/auth/change-password', data)
  },

  /**
   * Update admin profile
   */
  updateProfile: async (data: { name?: string; phone?: string; avatarUrl?: string }): Promise<any> => {
    const response = await apiClient.put('/admin/auth/profile', data)
    return response.data
  },

  /**
   * Get activity log
   */
  getActivityLog: async (): Promise<any> => {
    const response = await apiClient.get('/admin/auth/activity')
    return response.data
  },
}



